import {useTranslation} from '@/translations/i18n.tsx'
import {FC} from 'react'
import {UserContactsFormValidationSchema, UserExtended} from '@/features/users/types.ts'
import {Flexbox} from '@/components/ui/flexbox/FlexBox'
import {Mail01Icon, Phone01Icon, PhoneIcon} from '@/components/ui/icon/Icon'
import {useUserEditModeView} from '@/features/users/hooks/useUserEditModeView'
import {UserDetailsEditButtons} from '@/features/users/components/user-details-edit-buttons/UserDetailsEditButtons'
import {FormProvider, useForm, useFormContext} from 'react-hook-form'
import {UserContactsForm} from '@/features/users/components/user-contacts-form/UserContactsForm'
import {DetailsField, DetailsFieldsGrid, DetailsFieldsSection} from '@/components/ui/details-atoms/DetailsAtoms'
import {zodResolver} from '@hookform/resolvers/zod'
import {getUserDirtyFieldsFromFormValues, remapUserToPayload} from '@/features/users/utils'
import {useUserFormSubmission} from '@/features/users/hooks/useUserFormSubmission'
import {useUpdateUser} from '@/features/users/queries/useUpdateUser'
import {useUserDetailsStore} from '@/features/users/store/userDetailsStore'
import {Text} from '@/components/ui/text/Text'

interface UserDetailsInformationContactsDataProps {
    user: UserExtended
}

export const UserDetailsInformationContactsData: FC<UserDetailsInformationContactsDataProps> = ({user}) => {
    const form = useForm<UserContactsFormValidationSchema>({
        mode: 'onBlur',
        resolver: zodResolver(UserContactsFormValidationSchema),
        defaultValues: {
            contacts: user.contacts
                ? user.contacts.map(contact => ({
                      id: contact.id,
                      label: contact.label,
                      type: contact.type,
                      contact: contact.contact
                  }))
                : []
        }
    })

    return (
        <FormProvider {...form}>
            <UserDetailsInformationContactsDataContent user={user} />
        </FormProvider>
    )
}

const UserDetailsInformationContactsDataContent = ({user}: {user: UserExtended}) => {
    const {t} = useTranslation()
    const {isEditModeEnabled, isDisabledView} = useUserEditModeView('contacts')
    const form = useFormContext<UserContactsFormValidationSchema>()
    const setIsErrorBannerVisible = useUserDetailsStore(state => state.setIsErrorBannerVisible)
    const {onSuccessSubmit, onErrorSubmit, scrollToTop} = useUserFormSubmission({
        formSchema: UserContactsFormValidationSchema
    })

    // mutation
    const updateUser = useUpdateUser({
        urlParams: {userId: user.id},
        params: {responseType: 'extended'},
        options: {
            onSuccess: onSuccessSubmit,
            onError: onErrorSubmit
        }
    })

    // actions
    const handleSubmit = form.handleSubmit(
        formValues => {
            const dirtyFormValues = getUserDirtyFieldsFromFormValues(form.formState.dirtyFields, formValues)
            const parsedValues = remapUserToPayload(dirtyFormValues)
            return updateUser.mutate(parsedValues)
        },
        errors => {
            console.error(errors)
            setIsErrorBannerVisible(true)
            scrollToTop()
        }
    )

    return (
        <DetailsFieldsSection
            title={t('commons:contacts')}
            isDisabled={isDisabledView}
            headingEndSlot={
                <UserDetailsEditButtons
                    groupName="contacts"
                    isConfirmDisabled={!form.formState.isDirty}
                    onCancel={form.reset}
                    onConfirm={handleSubmit}
                />
            }
        >
            {isEditModeEnabled ? (
                <UserContactsForm />
            ) : (
                <DetailsFieldsGrid>
                    {user.contacts &&
                        user.contacts.map((contactData, index) => (
                            <DetailsField key={index}>
                                <Flexbox gap={1.5}>
                                    {contactData.type == 'telephone' ? (
                                        <PhoneIcon size={16} />
                                    ) : contactData.type == 'mobile_phone' ? (
                                        <Phone01Icon size={16} />
                                    ) : (
                                        <Mail01Icon size={16} />
                                    )}
                                    <div>
                                        <Text palette="neutral" shade="500" size="textSm">
                                            {t(`commons:${contactData.type}`)}
                                        </Text>
                                        <Text palette="neutral" shade="950" weight="500" size="textMd">
                                            {contactData.contact}
                                        </Text>
                                    </div>
                                </Flexbox>
                            </DetailsField>
                        ))}
                </DetailsFieldsGrid>
            )}
        </DetailsFieldsSection>
    )
}
